import { predicates } from "../predicates";
import type { ObjectTypeConfig } from "../types";

export const FILE_RECORD: ObjectTypeConfig = {
  actions: [
    {
      id: "open",
      surfaces: ["left-rail", "main-view", "context-menu"],
      presentation: "menu-item",
    },
    {
      // A3: canvas/chat files only — a workspace file is already there, a
      // reference points at one, and folders promote file-by-file for now.
      // Blocks promote like documents: the record carries its display facts
      // (metadata.display) wherever it goes.
      id: "add-to-workspace",
      surfaces: ["left-rail", "main-view", "context-menu", "floating-toolbar"],
      presentation: {
        "left-rail": "menu-item",
        "main-view": "menu-item",
        "context-menu": "menu-item",
        "floating-toolbar": "toolbar-utility",
      },
      hiddenWhen: {
        any: [
          { kind: { type: "file-record", class: "workspace" } },
          { kind: { type: "file-record", isReference: true } },
          { kind: { type: "file-record", isFolder: true } },
          predicates.sharedWithMeScope,
          predicates.favoritesScope,
          predicates.lockedOnCanvas,
        ],
      },
      disabledWhen: predicates.cannotEdit,
    },
    {
      // The tier marker mirroring add-to-workspace: a workspace file (or a
      // reference to one) states its home instead of offering promotion.
      id: "in-workspace",
      surfaces: ["left-rail", "main-view", "context-menu", "floating-toolbar"],
      presentation: {
        "left-rail": "menu-item",
        "main-view": "menu-item",
        "context-menu": "menu-item",
        "floating-toolbar": "toolbar-more-submenu",
      },
      hiddenWhen: {
        any: [
          // On the rail and the workspace pages the marker states where you
          // already are. It earns its place on a canvas, where "this lives
          // in the workspace" is news; elsewhere it is a disabled row saying
          // nothing.
          {
            all: [
              { kind: { type: "file-record", class: "workspace" } },
              {
                any: [
                  { surface: "left-rail" },
                  { surface: "main-view" },
                  { surface: "context-menu" },
                ],
              },
            ],
          },
          {
            all: [
              { not: { kind: { type: "file-record", class: "workspace" } } },
              { not: { kind: { type: "file-record", isReference: true } } },
              // Node surfaces resolve with the canvas-node kind — a reference
              // appearance there is the same workspace tier.
              {
                not: { kind: { type: "canvas-node", entityIsReference: true } },
              },
            ],
          },
        ],
      },
      // A marker, never a verb — disabled on every kind it renders for.
      disabledWhen: {
        any: [
          { kind: { type: "file-record" } },
          { kind: { type: "canvas-node" } },
        ],
      },
    },
    {
      id: "star",
      surfaces: ["left-rail", "main-view", "context-menu", "card-overlay"],
      presentation: {
        "left-rail": "menu-item",
        "main-view": "menu-item",
        "context-menu": "menu-item",
        "card-overlay": "corner-star",
      },
      hiddenWhen: {
        any: [
          predicates.isFavorite,
          { kind: { type: "file-record", isReference: true } },
          { kind: { type: "file-record", class: "canvas" } },
          { kind: { type: "file-record", class: "flow-session" } },
        ],
      },
    },
    {
      id: "unstar",
      surfaces: ["left-rail", "main-view", "context-menu", "card-overlay"],
      presentation: {
        "left-rail": "menu-item",
        "main-view": "menu-item",
        "context-menu": "menu-item",
        "card-overlay": "corner-star",
      },
      hiddenWhen: {
        any: [
          { not: predicates.isFavorite },
          { kind: { type: "file-record", isReference: true } },
          { kind: { type: "file-record", class: "canvas" } },
          { kind: { type: "file-record", class: "flow-session" } },
        ],
      },
    },

    {
      id: "rename",
      surfaces: ["left-rail", "main-view", "context-menu"],
      presentation: "menu-item",
      // A reference always displays its target's live name — rename the
      // workspace file instead.
      hiddenWhen: {
        any: [
          predicates.sharedWithMeScope,
          predicates.favoritesScope,
          predicates.cannotEdit,
          { kind: { type: "file-record", isReference: true } },
          predicates.lockedOnCanvas,
        ],
      },
    },
    {
      id: "move",
      surfaces: ["left-rail", "main-view", "context-menu"],
      presentation: "menu-item",
      hiddenWhen: {
        any: [
          predicates.sharedWithMeScope,
          predicates.favoritesScope,
          predicates.cannotMove,
          { kind: { type: "file-record", isReference: true } },
          // Allowlist, not a container blocklist: canvas-node entities can be
          // minimal shapes with no class, and those must read as container
          // files (whose one route out is Add to workspace), never as movable.
          { not: { kind: { type: "file-record", class: "workspace" } } },
        ],
      },
      disabledWhen: predicates.cannotMove,
    },
    {
      // Copy to travels with Move to, on workspace files only — a container
      // file's one route out is Add to workspace (A3), and a reference is an
      // appearance rather than a file. Deliberately carries no edit guard:
      // copying only reads the source, so viewing it is enough; write access
      // is judged on the destination, inside the picker and again server-side.
      id: "copy-to",
      surfaces: ["left-rail", "main-view", "context-menu"],
      presentation: "menu-item",
      hiddenWhen: {
        any: [
          predicates.sharedWithMeScope,
          predicates.favoritesScope,
          { kind: { type: "file-record", isReference: true } },
          // Same allowlist as move: only a workspace file copies from here.
          { not: { kind: { type: "file-record", class: "workspace" } } },
        ],
      },
    },
    {
      id: "share",
      surfaces: [
        "left-rail",
        "main-view",
        "context-menu",
        "floating-toolbar",
        "pane-header",
      ],
      presentation: {
        "left-rail": "menu-item",
        "main-view": "menu-item",
        "context-menu": "menu-item",
        "floating-toolbar": "toolbar-utility",
        "pane-header": "secondary-button",
      },
      // Sharing acts on the real file; a reference resolves to its target
      // server-side, so the verb stays available everywhere the file shows —
      // blocks included.
      hiddenWhen: predicates.cannotShare,
    },
    {
      id: "publish",
      surfaces: [
        "left-rail",
        "main-view",
        "context-menu",
        "floating-toolbar",
        "pane-header",
      ],
      presentation: {
        "left-rail": "menu-item",
        "main-view": "menu-item",
        "context-menu": "menu-item",
        "floating-toolbar": "toolbar-utility",
        "pane-header": "primary-button",
      },
      // Publishing through a reference publishes the TARGET (one publish
      // state per real file) — resolved server-side, so no isReference gate.
      // A published block serves its stored bytes (tokens inlined at write
      // time), so it renders as the full-page app it is.
      hiddenWhen: { not: { state: "showPublish" } },
    },
    {
      // Only offer Copy URL once the file is published — it copies the public
      // share URL, not the internal one. An unpublished file has no useful
      // public link to hand out.
      id: "copy-url",
      surfaces: ["left-rail", "main-view", "context-menu", "floating-toolbar"],
      presentation: {
        "left-rail": "menu-item",
        "main-view": "menu-item",
        "context-menu": "menu-item",
        "floating-toolbar": "toolbar-utility",
      },
      // The reference renders its target's publish state, so Copy URL
      // follows the target's published flag like every other surface.
      hiddenWhen: { not: { state: "isPublished" } },
    },
    {
      id: "download",
      surfaces: ["left-rail", "main-view", "context-menu", "floating-toolbar"],
      presentation: {
        "left-rail": "menu-item",
        "main-view": "menu-item",
        "context-menu": "menu-item",
        "floating-toolbar": "toolbar-utility",
      },
      variantSource: "file-record-export",
    },
    // Expand — a block's lightbox. Declared here with no surfaces only to
    // RESERVE this position beside Download: the file-record node config
    // (canvasNodeEntity.ts) overrides it in place, and an id first
    // introduced by an override would land at the end of the menu.
    { id: "expand", surfaces: [] },
    // Lock / Unlock — reserved here with no surfaces, like Expand: only the
    // canvas file-node config gives them one, so they never reach a list.
    { id: "lock", surfaces: [] },
    { id: "unlock", surfaces: [] },
    // Hide / Show on canvas — the non-destructive pair. Hide surfaces on the
    // canvas node menus (context menu + toolbar More) and on explorer rail
    // rows; Show only makes sense from the rail (a hidden node can't be
    // right-clicked on the board).
    {
      id: "remove-from-canvas",
      surfaces: ["left-rail", "context-menu", "floating-toolbar"],
      presentation: {
        "left-rail": "menu-item",
        "context-menu": "menu-item",
        "floating-toolbar": "toolbar-more-submenu",
      },
      hiddenWhen: {
        any: [
          {
            all: [
              { not: { scope: "canvas-explorer" } },
              { not: { state: "onCanvasDetail" } },
            ],
          },
          { state: "hiddenOnCanvas" },
          predicates.lockedOnCanvas,
        ],
      },
      disabledWhen: predicates.cannotEdit,
    },
    {
      id: "show-on-canvas",
      surfaces: ["left-rail"],
      presentation: "menu-item",
      hiddenWhen: {
        any: [
          { not: { scope: "canvas-explorer" } },
          { not: { state: "hiddenOnCanvas" } },
        ],
      },
      disabledWhen: predicates.cannotEdit,
    },
    {
      id: "delete",
      surfaces: ["left-rail", "main-view", "context-menu"],
      presentation: "menu-item",
      // Archive works from Favorites (it archives the file, same as archiving
      // a favorited canvas); shared-with-me stays read-only, and a viewer
      // without the permission doesn't see the verb at all.
      hiddenWhen: {
        any: [
          predicates.sharedWithMeScope,
          predicates.cannotDelete,
          { state: "onCanvasDetail" },
        ],
      },
    },

    {
      id: "version-history",
      surfaces: ["left-rail"],
      presentation: "menu-item",
      hiddenWhen: {
        any: [
          { not: { state: "showVersionHistory" } },
          { kind: { type: "file-record", isReference: true } },
        ],
      },
    },
    {
      id: "locate-on-canvas",
      surfaces: ["left-rail"],
      presentation: "menu-item",
      hiddenWhen: { not: { scope: "canvas-explorer" } },
    },
  ],
};

export const FILE_RECORD_FOLDER: ObjectTypeConfig = {
  actions: [
    {
      id: "open",
      surfaces: ["left-rail", "main-view", "context-menu"],
      presentation: "menu-item",
    },
    // A folder in the workspace tree holds canvases as well as files, so it
    // creates either one. Any workspace folder surface that can create
    // (rail rows, page cards and list rows) supplies the handler; the resolver
    // drops the action where none is supplied.
    {
      id: "new-canvas",
      surfaces: ["left-rail", "context-menu", "main-view"],
      presentation: "menu-item",
      hiddenWhen: {
        any: [
          predicates.cannotEdit,
          {
            not: {
              kind: { type: "file-record", class: "workspace", isFolder: true },
            },
          },
        ],
      },
    },
    // The file-tree verbs, handler-gated like new-canvas: every surface that
    // can create in this folder (rail rows, page cards and list rows) wires
    // the handlers; the resolver drops the actions where none is supplied.
    {
      id: "new-document",
      surfaces: ["left-rail", "context-menu", "main-view"],
      presentation: "menu-item",
      hiddenWhen: predicates.cannotEdit,
    },
    {
      id: "new-folder",
      surfaces: ["left-rail", "context-menu", "main-view"],
      presentation: "menu-item",
      hiddenWhen: predicates.cannotEdit,
    },
    {
      id: "upload-files",
      surfaces: ["left-rail", "context-menu", "main-view"],
      presentation: "menu-item",
      hiddenWhen: predicates.cannotEdit,
    },
    {
      id: "upload-folder",
      surfaces: ["left-rail", "context-menu", "main-view"],
      presentation: "menu-item",
      hiddenWhen: predicates.cannotEdit,
    },
    {
      id: "rename",
      surfaces: ["left-rail", "main-view", "context-menu"],
      presentation: "menu-item",
      hiddenWhen: predicates.cannotEdit,
    },
    {
      id: "move",
      surfaces: ["left-rail", "main-view", "context-menu"],
      presentation: "menu-item",
      hiddenWhen: predicates.cannotEdit,
    },
    // No Copy to on folders: a folder holds canvases now, and canvases have
    // no copy primitive — a "copy" that silently leaves them behind is worse
    // than none. Files inside copy individually.
    {
      id: "delete",
      surfaces: ["left-rail", "main-view", "context-menu"],
      presentation: "menu-item",
      hiddenWhen: predicates.cannotDelete,
    },
  ],
};
